import React, { useState } from 'react';
import { Button, Popconfirm, Tooltip } from 'antd';
import { notification, snackbar } from 'components/notifications/notifications';
import Icon from '@mdi/react';
import {
  mdiAlert,
  mdiCheckCircle,
  mdiCloseCircle,
  mdiProgressClock,
  mdiToolbox,
  mdiWrench,
} from '@mdi/js';
import Ninja from '@ninja';
import MetaAccountSetup from 'modules/apps/apps/MetaApp/components/MetaAccountSetup';
import GoogleAdsAccountSetup from 'modules/apps/apps/GoogleAdsApp/components/GoogleAdsAccountSetup';
import TiktokAccountSetup from 'modules/apps/apps/TiktokApp/components/TiktokAccountSetup';
import HubSpotAccountSetup from 'modules/apps/apps/HubSpotApp/components/HubSpotAccountSetup';
import useAccountConnectionFlow from 'components/AccountConnectionFlow/AccountConnectionFlow.provider';
import BingAdsAccountSetup from 'modules/apps/apps/BingAdsApp/components/BingAdsAccountSetup';
import UrlHelper from 'common/utils/url-helper';

const AccountSetupButton = ({ account, showButtonText = true }) => {
  const flow = useAccountConnectionFlow();
  const [loading, setLoading] = useState(false);

  const [setup, setSetup] = useState({});
  const setupStatus = account?.data?.setup_status;

  const setupAccount = () => {
    setLoading(true);
    flow.manage
      .requestSetup(account.id, setup)
      .then((res) => {
        notification.success(
          `Setup successfully requested`,
          `"${account.name}" setup requested. It may take a couple minutes. You will be notified once setup is completed`
        );
      })
      .catch((err) => {
        console.log(err);
        snackbar.error('Error while account setup request');
      })
      .finally(() => {
        setTimeout(() => setLoading(false), 500);
      });
  };

  const skipSetup = () => {
    setLoading(true);
    flow.manage
      .skipSetup(account.id)
      .then(() => {
        snackbar.success(`Setup skipped for "${account.name}"`);
      })
      .finally(() => {
        setTimeout(() => setLoading(false), 500);
      });
  };

  const SetupDescription = setupsByNetwork[account.network];

  const setupConfigs = {
    manual: {
      icon: <Icon path={mdiWrench} color={Ninja.colors.warning} />,
      tooltip: (
        <>
          Requires manual setup of attribution,{' '}
          <a rel='noopener noreferrer' target='_blank' href={UrlHelper.docs('docs/tracking')}>
            How to attribute?
          </a>
        </>
      ),
      disabled: true,
      buttonText: 'Manual Setup',
    },
    requested: {
      icon: <Icon path={mdiProgressClock} color={Ninja.colors.warning} />,
      tooltip: 'Set up already requested, you will be notified when it is completed',
      disabled: true,
      buttonText: 'Set Up Requested',
    },
    completed: {
      icon: <Icon path={mdiCheckCircle} color={Ninja.colors.green} />,
      tooltip: 'Set up already completed',
      disabled: true,
      buttonText: '',
    },
    error: {
      icon: <Icon path={mdiCloseCircle} color={Ninja.colors.red} />,
      tooltip: 'Error while setup, please contact support',
      disabled: true,
      buttonText: 'Set up error',
    },
    skipped: {
      icon: <Icon path={mdiAlert} color={Ninja.colors.warning} />,
      tooltip: 'Skipped setup',
      disabled: false,
      buttonText: 'Skipped Setup',
    },
    none: {
      icon: <Icon path={mdiCloseCircle} color={Ninja.colors.red} />,
      tooltip: 'Set up account',
      disabled: false,
      buttonText: 'Set Up Account',
    },
  };

  const setupConfig = setupConfigs[setupStatus ?? 'none'] ?? null;

  if (!setupConfig) {
    return 'setup error';
  }

  return (
    <Popconfirm
      placement='top'
      title='MyDataNinja Setup'
      description={<SetupDescription onChange={setSetup} /> ?? 'Setup'}
      rootClassName='max-w-[400px]'
      okText='Set Up'
      cancelText='Skip For Now'
      okButtonProps={{ loading: loading, size: 'middle' }}
      cancelButtonProps={{
        icon: <Icon path={mdiAlert} color={Ninja.colors.warning} />,
        size: 'middle',
        loading: loading,
      }}
      onConfirm={() => setupAccount(false)}
      onCancel={() => skipSetup()}
    >
      {!account.is_manager ? (
        <Tooltip title={setupConfig.tooltip}>
          <Button
            size='medium'
            icon={setupConfig.icon}
            loading={loading}
            disabled={setupConfig.disabled}
          >
            {showButtonText && setupConfig.buttonText}
          </Button>
        </Tooltip>
      ) : null}
    </Popconfirm>
  );
};

const setupsByNetwork = {
  facebook: MetaAccountSetup,
  google: GoogleAdsAccountSetup,
  tiktok: TiktokAccountSetup,
  bing: BingAdsAccountSetup,
  hubspot: HubSpotAccountSetup,
};

AccountSetupButton.propTypes = {};

export default AccountSetupButton;
